/**
 * Install-EdgePACS-Companion dialog. Shown by the worklist / viewer ONLY
 * when the deep link failed to launch (i.e. the protocol handler isn't
 * registered on this machine). The `launchCompanion` helper in
 * composerHandoff.ts handles the detection.
 *
 * Two states:
 *   1. `prompt`  - initial view. Explains why nothing happened, offers
 *                  the Install button that triggers the download.
 *   2. `installing` - after Install is clicked. Download has fired; the
 *                     dialog walks the user through the next few clicks
 *                     Windows needs (Keep in Chrome bar, Run installer,
 *                     UAC prompt). Includes a Retry button that fires the
 *                     original action again - by the time the user has
 *                     installed and comes back, one click completes.
 */
import { useEffect, useState } from 'react';

interface Props {
  /** Short label describing what triggered this dialog, e.g. "Burning 3 studies"
   *  or "Sending 5 images to Composer". Shown as the dialog title. */
  action: string;
  /** URL to the Companion Windows installer. Downloaded when the user
   *  clicks the Install button. */
  installerUrl: string;
  /** Fired when the user clicks Close, presses Esc, or clicks the backdrop.
   *  Also fired by the "Retry" button in the installing state - caller
   *  should then re-fire the original deep-link action. */
  onClose: () => void;
  /** Optional. If provided, the "Retry launch" button will call this
   *  instead of onClose - so the caller can re-fire the deep link
   *  immediately once the user says the install is done. */
  onRetry?: () => void;
}

export default function CompanionLaunchDialog({ action, installerUrl, onClose, onRetry }: Props) {
  const [stage, setStage] = useState<'prompt' | 'installing'>('prompt');

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function startInstall() {
    // Trigger the download programmatically. Using a hidden <a download>
    // avoids navigating the current tab away, so the dialog stays put
    // and we can continue guiding the user through the remaining steps.
    const a = document.createElement('a');
    a.href = installerUrl;
    a.download = '';   // hint browser to save-as, not navigate
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setStage('installing');
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="companion-launch-title"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0, 0, 0, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 10000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#1a2a3a', color: '#fff',
          borderRadius: 8, padding: '24px 28px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
          maxWidth: 500, width: '90%',
          fontSize: 14, lineHeight: 1.5,
        }}
      >
        <h3 id="companion-launch-title" style={{ margin: '0 0 12px', fontSize: 18 }}>
          {stage === 'prompt' ? 'EdgePACS Companion required' : 'Installing EdgePACS Companion'}
        </h3>

        {stage === 'prompt' ? (
          <>
            <p style={{ margin: '0 0 12px' }}>
              <strong>{action}</strong> needs the EdgePACS Companion running on this
              computer, and it doesn't look like it's installed yet.
            </p>
            <p style={{ margin: '0 0 20px', opacity: 0.85 }}>
              Install it once and you're set - all future compose, burn, and
              export actions will just work.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  background: 'transparent', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.35)',
                  borderRadius: 6, fontSize: 14, cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={startInstall}
                style={{
                  padding: '8px 18px',
                  background: '#2b7cff', color: '#fff',
                  border: '1px solid #2b7cff',
                  borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                }}
              >
                Install EdgePACS Companion
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ margin: '0 0 12px' }}>
              The installer is downloading now. Because browsers can't run
              installers directly, take these last two steps:
            </p>
            <ol style={{ margin: '0 0 20px 18px', padding: 0, opacity: 0.9 }}>
              <li style={{ marginBottom: 6 }}>
                Chrome may ask <em>"Keep or Discard"</em> - choose <strong>Keep</strong>.
              </li>
              <li style={{ marginBottom: 6 }}>
                Open the downloaded <code>EdgePACS-Companion-Setup.exe</code>
                from your Downloads folder (or the Chrome download bar) and follow
                the installer.
              </li>
              <li>
                When it's done, click <strong>Retry launch</strong> below.
              </li>
            </ol>
            <p style={{ margin: '0 0 16px', opacity: 0.7, fontSize: 12 }}>
              Not seeing the download? <a
                href={installerUrl}
                style={{ color: '#bee3f8', textDecoration: 'underline' }}
                rel="noopener"
              >Click here to try again</a>.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button
                onClick={onClose}
                style={{
                  padding: '8px 16px',
                  background: 'transparent', color: '#fff',
                  border: '1px solid rgba(255,255,255,0.35)',
                  borderRadius: 6, fontSize: 14, cursor: 'pointer',
                }}
              >
                Close
              </button>
              {onRetry && (
                <button
                  onClick={() => { onClose(); onRetry(); }}
                  style={{
                    padding: '8px 18px',
                    background: '#2b7cff', color: '#fff',
                    border: '1px solid #2b7cff',
                    borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Retry launch
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
